import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { Ruler, X } from 'lucide-react';

interface FullscreenFloorPlanViewerProps {
  floorPlan: string;
  floorPlanImages?: string[];
  apartmentName: string;
  isOpen: boolean;
  onClose: () => void;
}

const FullscreenBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.95);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2147483647;
  opacity: 0;
  animation: fadeIn 0.3s forwards;

  @keyframes fadeIn {
    to {
      opacity: 1;
    }
  }
  
  @media (max-width: 850px) {
    background: rgba(0, 0, 0, 0.98);
  }
`;

const FullscreenContainer = styled.div`
  width: 95%;
  height: 95%;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  transform: scale(0.9);
  animation: scaleIn 0.3s forwards;

  @keyframes scaleIn {
    to {
      transform: scale(1);
    }
  }

  @media (max-width: 850px) {
    width: 100%;
    height: 100%;
    border-radius: 0;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  position: relative;
  z-index: 100;
  flex-shrink: 0;

  @media (max-width: 850px) {
    padding: 16px 20px;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 250, 252, 0.9));
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    box-shadow: 0 2px 20px rgba(0, 0, 0, 0.05);
    min-height: 76px;
    position: sticky;
    top: 0;
  }
`;

const Title = styled.h3`
  margin: 0;
  color: white;
  font-size: 18px;
  font-weight: 600;
  font-family: 'Inter', sans-serif;

  @media (max-width: 850px) {
    font-size: 18px;
    font-weight: 700;
    color: #111827;
  }
`;

const CloseButton = styled.button`
  background: #f1f3f4;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 18px;
  color: #5f6368;
  transition: all 0.2s ease;
  z-index: 1000;
  line-height: 1;
  padding: 0;
  text-align: center;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background: #e8eaed;
      transform: rotate(90deg);
    }
  }

  @media (max-width: 850px) {
    width: 48px;
    height: 48px;
    font-size: 20px;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 250, 252, 0.9));
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.3);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.4);
    color: #374151;
    font-weight: 600;
    min-height: 48px;
    min-width: 48px;
    position: relative;
    
    &:active {
      transform: scale(0.95);
      transition: transform 0.1s ease;
    }
  }
`;

const PDFContainer = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  overflow: auto;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 850px) {
    background: #f8fafc;
    padding: 16px;
    align-items: flex-start;
  }
`;

const FloorPlanImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;
  margin: auto;

  @media (max-width: 850px) {
    width: 100%;
    height: auto;
    max-height: none;
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  }
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #dc3545;
  font-size: 16px;
  text-align: center;
  padding: 20px;
`;

const LoadingMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #666;
  font-size: 16px;
  text-align: center;
  padding: 20px;
`;

const FullscreenFloorPlanViewer: React.FC<FullscreenFloorPlanViewerProps> = ({
  floorPlan,
  floorPlanImages = [],
  apartmentName,
  isOpen,
  onClose
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 850);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const previewImage = floorPlanImages[0];
  const useImagePreview = isMobile && !!previewImage;

  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      setHasError(false);
    }
  }, [isOpen]);

  const handleLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const content = (
    <FullscreenBackdrop onClick={handleBackdropClick}>
      <FullscreenContainer>
        <Header>
          <Title>
            <Ruler size={16} style={{ display: 'inline-block', marginRight: '8px' }} />
            Grundriss für {apartmentName}
          </Title>
          <CloseButton onClick={onClose} aria-label="Grundriss schließen">
            <X size={20} />
          </CloseButton>
        </Header>

        <PDFContainer>
          {isLoading && !hasError && (
            <LoadingMessage>
              Grundriss wird geladen...
            </LoadingMessage>
          )}
          
          {hasError && (
            <ErrorMessage>
              Grundriss konnte nicht geladen werden
            </ErrorMessage>
          )}

          {useImagePreview ? (
            <FloorPlanImage
              src={previewImage}
              alt={`Grundriss für ${apartmentName}`}
              onLoad={handleLoad}
              onError={handleError}
              style={{ display: isLoading || hasError ? 'none' : 'block' }}
            />
          ) : (
            <iframe
              src={`${floorPlan}#toolbar=1&navpanes=0&scrollbar=1&view=FitH`}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                display: isLoading || hasError ? 'none' : 'block'
              }}
              title={`Grundriss für ${apartmentName}`}
              onLoad={handleLoad}
              onError={handleError}
            />
          )}
        </PDFContainer>
      </FullscreenContainer>
    </FullscreenBackdrop>
  );

  return createPortal(content, document.body);
};

export default FullscreenFloorPlanViewer;
